// ============================================
// INFRASTRUCTURE - Auth Repository
// ============================================

import { axiosClient } from './axios-client';
import type { Usuario, AuthTokens } from '../../domain/entities';
import type { LoginDto, RegisterDto } from '../../domain/dtos';

export const authRepository = {
  /**
   * Login de usuario
   */
  async login(dto: LoginDto): Promise<{ 
    token: string; 
    expiresInSeconds: number;
    usuarioId: number; 
    nombre: string; 
    apellidos: string; 
    almacenId: number; 
    permisos: string[] 
  }> {
    const { data } = await axiosClient.post<{ 
      token: string; 
      expiresInSeconds: number;
      usuarioId: number; 
      nombre: string; 
      apellidos: string; 
      almacenId: number; 
      permisos: string[] 
    }>(
      '/auth/login',
      dto
    );
    return data;
  },

  /**
   * Registro de nuevo almacén y usuario admin
   */
  async register(dto: RegisterDto): Promise<{ usuario: Usuario; tokens: AuthTokens }> {
    const { data } = await axiosClient.post<{ usuario: Usuario; tokens: AuthTokens }>(
      '/auth/register',
      dto
    );
    return data;
  },

  /**
   * Obtener perfil del usuario autenticado
   */
  async getProfile(): Promise<Usuario> {
    const { data } = await axiosClient.get<Usuario>('/auth/profile');
    return data;
  },

  /**
   * Logout (invalidar refresh token en cookie)
   */
  async logout(): Promise<void> {
    await axiosClient.post('/auth/logout');
  },

  /**
   * Refresh access token usando cookie HttpOnly
   */
  async refreshAccessToken(): Promise<{ token: string; expiresInSeconds: number }> {
    const { data } = await axiosClient.post<{ token: string; expiresInSeconds: number }>(
      '/auth/refresh',
      {} // Body vacío, la cookie se envía automáticamente
    );
    return data;
  },

  /**
   * Iniciar proceso de registro de almacén (envía correo de verificación)
   */
  async signupInitiate(dto: {
    rutAlmacen: string;
    runPersonal: string;
    nombrePropietario: string;
    apellidosPropietario: string;
    email: string;
    telefono: number;
    medioInformacion: string;
  }): Promise<void> {
    await axiosClient.post('/auth/signup/initiate', dto);
  },

  /**
   * Completar registro de almacén
   */
  async completeSignup(dto: {
    usuarioId: number;
    almacenId: number;
    nombreComercial: string;
    direccion: string;
    numeroDireccion: number;
    local: string | null;
    tienePatenteAlcohol: boolean;
    tieneFrios: boolean;
    tieneCongelados: boolean;
    vendePorMayor: boolean;
    tamanoLocal: string;
    tipoAlmacen: string;
    claveSii: string | null;
    clavePropietario: string;
    nombreVendedor: string;
    claveVendedor: string;
  }): Promise<{ 
    token: string; 
    expiresInSeconds: number;
    usuarioId: number; 
    nombre: string; 
    apellidos: string; 
    almacenId: number; 
    permisos: string[] 
  }> {
    const { data } = await axiosClient.post<{ 
      token: string; 
      expiresInSeconds: number;
      usuarioId: number; 
      nombre: string; 
      apellidos: string; 
      almacenId: number; 
      permisos: string[] 
    }>(
      '/auth/signup/complete',
      dto
    );
    return data;
  },

  /**
   * Verificar estado de registro de almacén y usuario
   */
  async checkSignupStatus(userId: string, almacenId: string): Promise<{
    usuarioId: number;
    almacenId: number;    nombreUsuario: string;    tipoAlmacenValido: boolean;
  }> {
    const { data } = await axiosClient.get(
      `/auth/signup/check-status/${userId}?almacenId=${almacenId}`
    );
    return data;
  },

  /**
   * Solicitar código de restablecimiento de contraseña
   */
  async requestPasswordReset(rutAlmacen: string): Promise<{
    email: string;
    message: string;
  }> {
    const { data } = await axiosClient.post('/auth/password-reset/request', {
      rutAlmacen,
    });
    return data;
  },

  /**
   * Verificar código de restablecimiento de contraseña
   */
  async verifyPasswordResetCode(rutAlmacen: string, code: string): Promise<{
    valid: boolean;
    expiresAt: string;
  }> {
    const { data } = await axiosClient.post('/auth/password-reset/verify', {
      rutAlmacen,
      code,
    });
    return data;
  },

  /**
   * Confirmar cambio de contraseña con código
   */
  async confirmPasswordReset(rutAlmacen: string, code: string, newPassword: string): Promise<void> {
    await axiosClient.post('/auth/password-reset/confirm', {
      rutAlmacen,
      code,
      newPassword,
    });
  },

  /**
   * Impersonación de almacén para uso interno del equipo (solo dev/staging)
   */
  async devImpersonate(rutAlmacen: string, masterKey: string): Promise<{
    token: string;
    expiresInSeconds: number;
    usuarioId: number;
    nombre: string;
    apellidos: string;
    almacenId: number;
    permisos: string[];
  }> {
    const { data } = await axiosClient.post<{
      token: string;
      expiresInSeconds: number;
      usuarioId: number;
      nombre: string;
      apellidos: string;
      almacenId: number;
      permisos: string[];
    }>('/public/dev/login', { rutAlmacen, masterKey });
    return data;
  },
};
